import { create } from 'zustand';
import type { Channel } from '@/features/channels/types';

/**
 * Channel UI state management
 * Handles:
 * - Channel selection
 * - Modal states for channel operations
 * - Channel type management
 * 
 * @see src/stores/importStore.ts for import specific state
 */

interface ContextParams {
  regionId?: number;
}

interface ChannelState {
  selectedChannels: Record<string, boolean>;
  editingChannel: Channel | null;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isTypeManagerOpen: boolean;
  isImportModalOpen: boolean;
  currentRegionId?: number;
  setSelectedChannels: (channels: Record<string, boolean>) => void;
  toggleChannelSelection: (id: number) => void;
  openCreateModal: (params?: ContextParams) => void;
  closeCreateModal: () => void;
  openEditModal: (channel: Channel) => void;
  closeEditModal: () => void;
  openDeleteModal: (channel?: Channel) => void;
  closeDeleteModal: () => void;
  openTypeManagerModal: () => void;
  closeTypeManagerModal: () => void;
  openImportModal: () => void;
  closeImportModal: () => void;
  clearSelectedChannels: () => void;
  setEditingChannel: (channel: Channel | null) => void;
  setIsEditOpen: (isOpen: boolean) => void;
}

export const useChannelStore = create<ChannelState>((set) => ({
  // State
  selectedChannels: {},
  editingChannel: null,
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  isTypeManagerOpen: false,
  isImportModalOpen: false,
  currentRegionId: undefined,

  // Channel Actions
  setSelectedChannels: (channels) => set({ selectedChannels: channels }),
  toggleChannelSelection: (id) => set((state) => ({
    selectedChannels: { ...state.selectedChannels, [id]: !state.selectedChannels[id] }
  })),

  // Modal Actions
  openCreateModal: (params) => set({ 
    isCreateModalOpen: true,
    currentRegionId: params?.regionId
  }),
  closeCreateModal: () => set({ isCreateModalOpen: false }),
  openEditModal: (channel) => set({ editingChannel: channel, isEditModalOpen: true }),
  closeEditModal: () => set({ editingChannel: null, isEditModalOpen: false }),
  openDeleteModal: (channel) => set({ editingChannel: channel, isDeleteModalOpen: true }),
  closeDeleteModal: () => set({ editingChannel: null, isDeleteModalOpen: false }),
  openTypeManagerModal: () => set({ isTypeManagerOpen: true }),
  closeTypeManagerModal: () => set({ isTypeManagerOpen: false }),
  openImportModal: () => set({ isImportModalOpen: true }),
  closeImportModal: () => set({ isImportModalOpen: false }),
  clearSelectedChannels: () => set({ selectedChannels: {} }),
  setEditingChannel: (channel) => set({ editingChannel: channel }),
  setIsEditOpen: (isOpen) => set({ isEditModalOpen: isOpen }),
}));